import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  Paper,
  Icon,
  IconButton,
} from "@mui/material";
import { AiActions } from "../../../Services/_actions/index.js";
import MathText from "./MathText.tsx";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ResultPage: React.FC = () => {
  const { getTestApi } = AiActions();
  const { resultId } = useParams<{ resultId: string }>();
  const location = useLocation();
  const testData = location?.state?.test || null;
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const formatDuration = (start: any, end: any) => {
    if (!start || !end) return "—";
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    const diff = Math.floor((e - s) / 1000);
    const m = Math.floor(diff / 60);
    const s2 = diff % 60;
    return m > 0 ? `${m}m ${s2}s` : `${s2}s`;
  };

  const fetchTestData = async () => {
    try {
      setLoading(true);
      const response = await getTestApi({ testId: resultId, showAnswers: 1 });
      const data = response?.data?.data || response?.data;

      if (!data?.test_started) {
        setResult(null);
        return;
      }
      const serverAnswers = data.user_answers || {};
      const detailed = data.detailed_responses || [];

     const safeStr = (val: any): string => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    return val.text || val.value || val.label || val.content || JSON.stringify(val);
  }
  return String(val);
};

const formattedQuestions = data.questions.map((q: any) => {
  const userAnswer = serverAnswers[q.question_id] || "";
  const detail = detailed.find(
    (d: any) => d.question_id === q.question_id
  );

  const correctAnswer = safeStr(detail?.correct_answer || q.correct_answer);

  const options = q.options.map((opt: any) => {
    // opt could be a string OR an object like {text, value, ...}
    const optValue = safeStr(typeof opt === "object" ? (opt.value || opt.text) : opt);
    const isSelected = optValue === userAnswer;
    const isCorrect = optValue === correctAnswer;

    return {
      value: optValue,
      isSelected,
      isCorrect,
      reasoning:
        isCorrect || (isSelected && !isCorrect)
          ? safeStr(detail?.reasoning)
          : undefined,
    };
  });

  return {
    ...q,
    question: safeStr(q.question), // ensure question is always a string
    options,
    correct_answer: correctAnswer,
    user_answer: userAnswer,
  };
});

      const correctCount = detailed.filter((d: any) => d.is_correct).length;
      const attemptedCount = data.answered_questions || 0;
      const incorrectCount = attemptedCount - correctCount;
      const unattemptedCount = data.total_questions - attemptedCount;

      setResult({
        ...data,
        test_name: `${data.filled_fields.subject} - ${data?.test_type || data?.filled_fields?.topic} `,
        solutions: formattedQuestions,
        correct_count: correctCount,
        incorrect_count: incorrectCount,
        attemptedCount,
        unattemptedCount,
        total_marks: data.total_questions,
        score: correctCount,
        time_taken: formatDuration(data.start_time, data.end_time),
      });
    } catch (e) {
      console.error("Result load failed", e);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resultId) fetchTestData();
  }, [resultId]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography mt={2} color="text.secondary">
          Loading result…
        </Typography>
      </Box>
    );

  if (!result)
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <Typography color="error" variant="h6">
          ❌ Result not found
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        position: "unset",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f5f5f5",
      }}
    >
      {/* ===== Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "#fff",
          borderBottom: "2px solid #cdcdcd",
          p: 2,
          zIndex: 10,
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            {result.test_name}
          </Typography>

          <IconButton
            size="small"
            onClick={() => {
              if (!testData) return;

              navigate(
                `/r/student/concept-builder/${testData?.context_id}/${testData?.phone_number}/?subject=${testData?.filled_fields?.subject}&topic=${testData?.filled_fields?.topic}&subtopic=${testData?.filled_fields?.subtopic}`,
                {
                  state: {
                    subject: testData?.filled_fields?.subject,
                    topic: testData?.filled_fields?.topic,
                    subtopic: testData?.filled_fields?.subtopic,
                  },
                }
              );
            }}
          >
            <ArrowBackIcon />
          </IconButton>

        </Box>

        <Grid container spacing={2}>
          {[
            {
              label: "Score",
              value: `${result.score}/${result.total_marks}`,
              color: "#1976d2",
            },
            {
              label: "Correct",
              value: result.correct_count,
              color: "#2e7d32",
            },
            {
              label: "Incorrect",
              value: result.incorrect_count,
              color: "#d32f2f",
            },
            {
              label: "Attempted",
              value: result.attemptedCount,
              color: "#0288d1",
            },
            {
              label: "Unattempted",
              value: result.unattemptedCount,
              color: "#757575",
            },
            { label: "Time", value: result.time_taken, color: "#424242" },
          ].map(({ label, value, color }) => (
            <Grid item xs={4} md={2} key={label}>
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  border: "1px solid #d6d6d6",
                  borderRadius: 1,
                  bgcolor: "#fff",
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '14px' }}>
                  {label}
                </Typography>
                <Typography fontWeight={700} sx={{ color, mt: 0.5, fontSize: '14px' }}>
                  {value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {location?.state?.violations?.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "#fff3e0",
              border: "1px solid #ffe0b2",
              borderRadius: 1,
            }}
          >
            {location.state.violations.map((v: any, i: number) => (
              <Typography key={i} variant="body2" color="warning.dark">
                ⚠️ {v.type} at {v.timestamp}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      {/* ===== Questions ===== */}
      <Box sx={{ height: '70vh', position: 'relative', overflowY: 'auto' }}>
        <Box sx={{ flex: 1, overflowY: "hidden", p: 2 }}>
          {result.solutions.map((q: any, i: number) => (
            <Card
              key={q.question_id}
              elevation={0}
              sx={{
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <CardContent>
                <Typography fontWeight={700} gutterBottom>
                  Q{i + 1}. <MathText text={q.question} />
                </Typography>

                <Stack mt={2} spacing={1}>
                  {q.options.map((o: any, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderLeft: o.isCorrect
                          ? "4px solid #4caf50"
                          : o.isSelected
                            ? "4px solid #f44336"
                            : "none",
                        bgcolor: o.isCorrect
                          ? "rgba(76, 175, 80, 0.08)"
                          : o.isSelected
                            ? "rgba(244, 67, 54, 0.08)"
                            : "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                          }}
                        >
                          <MathText text={o.value} />
                        </Typography>
                       {o.isSelected && (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography
      variant="caption"
      sx={{
        bgcolor: o.isCorrect ? "success.light" : "error.light",
        color: o.isCorrect ? "success.dark" : "error.dark",
        px: 1,
        py: 0.3,
        borderRadius: 1,
        fontWeight: 600,
      }}
    >
      {o.isCorrect ? "Correct" : "Incorrect"}
    </Typography>

    <Typography
      variant="caption"
      color="primary"
      sx={{ fontWeight: 500 }}
    >
      Your Selection
    </Typography>
  </Stack>
)}

                      </Box>
                      {o.reasoning && (
  <Box
    sx={{
      mt: 1,
      p: 1,
      borderRadius: 1,
      bgcolor: o.isCorrect
        ? "rgba(76, 175, 80, 0.08)"
        : "rgba(244, 67, 54, 0.08)",
      border: `1px solid ${
        o.isCorrect ? "#4caf50" : "#f44336"
      }`,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        color: o.isCorrect ? "success.dark" : "error.dark",
        display: "block",
        mb: 0.5,
      }}
    >
      {o.isCorrect ? "Explanation (Correct)" : "Explanation (Why Incorrect)"}
    </Typography>

    <Typography variant="caption">
      <MathText text={o.reasoning} />
    </Typography>
  </Box>
)}

                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ResultPage;